import React, { useEffect, useRef } from 'react';

// Multi-image picker: choose files, paste (Ctrl+V) an image straight from the
// clipboard, and remove any of the queued images before they're actually
// uploaded. `images` is an array of { id, file, previewUrl }.
export default function MultiImagePicker({ images, onChange, label = 'Attach Images (optional)' }) {
  const inputRef = useRef(null);
  const imagesRef = useRef(images);
  imagesRef.current = images;

  useEffect(() => () => {
    imagesRef.current.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (fileList) => {
    const newImages = Array.from(fileList || [])
      .filter((f) => f.type.startsWith('image/'))
      .map((file) => ({
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
    if (newImages.length > 0) onChange((prev) => [...prev, ...newImages]);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles = [];
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) pastedFiles.push(file);
      }
    }
    if (pastedFiles.length > 0) {
      e.preventDefault();
      addFiles(pastedFiles);
    }
  };

  const removeImage = (id) => {
    onChange((prev) => {
      const target = prev.find((img) => img.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((img) => img.id !== id);
    });
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-500">{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        onPaste={handlePaste}
        tabIndex={0}
        role="button"
        className="cursor-pointer rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-xs text-slate-500 outline-none transition hover:border-indigo-300 hover:bg-indigo-50/40 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
      >
        Click to choose image(s), or click here and press Ctrl+V to paste a copied image
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          className="hidden"
        />
      </div>
      {images.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {images.map((img) => (
            <div key={img.id} className="group relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-slate-200">
              <img src={img.previewUrl} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeImage(img.id); }}
                className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-white hover:bg-red-600"
                aria-label="Remove image"
              >
                <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
